import type { TError, TPostWithAuthor } from '@onesource/schemas';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

interface ToggleSavePostInput {
  postId: string;
  /** true = save (bookmark), false = remove the save */
  save: boolean;
}

/**
 * Save / unsave (bookmark) a post for the current user. Optimistically flips
 * `isSaved` on the post across all feed and single-post queries (same
 * patching strategy as useToggleReaction), then reconciles on settle so the
 * "Saved posts" quick filter refetches with the new state.
 */
export const useToggleSavePost = () => {
  const queryClient = useQueryClient();

  return useMutation<void, TError, ToggleSavePostInput>({
    mutationFn: async ({ postId, save }) => {
      if (save) {
        await axios.post(`/community-hub/posts/${postId}/save`);
      } else {
        await axios.delete(`/community-hub/posts/${postId}/save`);
      }
    },
    onMutate: async ({ postId, save }) => {
      await queryClient.cancelQueries({ queryKey: [ENTITY, 'posts'] });

      const patchPost = (post: TPostWithAuthor): TPostWithAuthor =>
        post.id === postId ? { ...post, isSaved: save } : post;

      // Infinite feed queries
      queryClient.setQueriesData<InfiniteData<{ posts: TPostWithAuthor[]; total: number }>>(
        { queryKey: [ENTITY, 'posts', 'infinite'] },
        old => {
          if (!old) {
            return old;
          }
          return { ...old, pages: old.pages.map(page => ({ ...page, posts: page.posts.map(patchPost) })) };
        },
      );

      // Flat list queries
      queryClient.setQueriesData<TPostWithAuthor[]>(
        { queryKey: [ENTITY, 'posts'], predicate: q => !q.queryKey.includes('infinite') },
        old => (Array.isArray(old) ? old.map(patchPost) : old),
      );

      // Single post (detail) query
      queryClient.setQueriesData<TPostWithAuthor>({ queryKey: [ENTITY, 'posts', 'detail', postId] }, old =>
        old ? patchPost(old) : old,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ENTITY, 'posts'] });
    },
  });
};
